import React, { useState, useEffect, useMemo } from "react";
import Icon from "../../../components/Icon";
import Modal from "../../../components/Modal";
import {
  StatusBadge,
  getApplicationUnifiedStatus,
} from "../../../components/Badge";
import Skeleton from "../../../components/Skeleton";
import { EmptyState } from "../../../components/State";
import Pagination from "../../../components/Pagination";
import SearchInput from "../../../components/SearchInput";
import ConfirmModal from "../../../components/ConfirmModal";
import ServerServicePickerModal from "../../../components/ServerServicePickerModal";
import {
  fetchApplicationTree,
  fetchApplications,
  createApplication,
  updateApplication,
  deleteApplication,
  detachServiceFromApplication,
} from "../../ops/services/CMDBService";

export default function ApplicationsView() {
  const [tree, setTree] = useState([]);
  const [allApps, setAllApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 8;
  const [expandedParents, setExpandedParents] = useState(new Set());

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    owner_team: "",
    parent_id: "",
  });

  const [pickerTargetApp, setPickerTargetApp] = useState(null);
  const [deletingApp, setDeletingApp] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [treeData, flatApps] = await Promise.all([
        fetchApplicationTree(),
        fetchApplications(true),
      ]);
      const appTree = treeData || [];
      setTree(appTree);
      setAllApps(flatApps || []);
      // Expand all parent nodes by default
      const parentIds = new Set(appTree.map((p) => p.id));
      setExpandedParents(parentIds);
    } catch (err) {
      console.warn("Failed to load application hierarchy:", err);
      setTree([]);
      setAllApps([]);
    } finally {
      setLoading(false);
    }
  }

  function toggleParentExpand(id) {
    const next = new Set(expandedParents);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setExpandedParents(next);
  }

  function expandAll() {
    setExpandedParents(new Set(tree.map((p) => p.id)));
  }

  function collapseAll() {
    setExpandedParents(new Set());
  }

  function handleOpenCreate(parentId = "") {
    setEditingApp(null);
    setFormData({
      name: "",
      code: "",
      description: "",
      owner_team: "",
      parent_id: parentId || "",
    });
    setIsEditModalOpen(true);
  }

  function handleOpenEdit(app) {
    setEditingApp(app);
    setFormData({
      name: app.name || "",
      code: app.code || "",
      description: app.description || "",
      owner_team: app.owner_team || "",
      parent_id: app.parent_id || "",
    });
    setIsEditModalOpen(true);
  }

  async function handleSaveApplication(e) {
    e.preventDefault();
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim() || null,
        description: formData.description.trim() || null,
        owner_team: formData.owner_team.trim() || null,
        parent_id: formData.parent_id ? formData.parent_id : null,
      };

      if (editingApp) {
        await updateApplication(editingApp.id, {
          ...payload,
          clear_parent: !formData.parent_id,
        });
      } else {
        await createApplication(payload);
      }
      setIsEditModalOpen(false);
      await loadData();
    } catch (err) {
      alert("Error saving application: " + (err.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteApplication() {
    if (!deletingApp) return;
    setSaving(true);
    try {
      await deleteApplication(deletingApp.id);
      setDeletingApp(null);
      await loadData();
    } catch (err) {
      alert(
        "Failed to decommission application: " +
          (err.message || "Unknown error"),
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleUnlinkService(appId, serviceId, serviceName) {
    if (
      !window.confirm(`Unlink service "${serviceName}" from this application?`)
    )
      return;
    try {
      await detachServiceFromApplication(appId, serviceId);
      await loadData();
    } catch (err) {
      alert("Failed to unlink service: " + (err.message || "Unknown error"));
    }
  }

  const forbiddenParentIds = useMemo(() => {
    if (!editingApp) return new Set();
    const ids = new Set([editingApp.id]);
    const findChildren = (parentIds) => {
      let added = false;
      allApps.forEach((a) => {
        if (a.parent_id && parentIds.has(a.parent_id) && !ids.has(a.id)) {
          ids.add(a.id);
          added = true;
        }
      });
      if (added) findChildren(ids);
    };
    findChildren(ids);
    return ids;
  }, [editingApp, allApps]);

  // Executive summary counts
  const stats = useMemo(() => {
    const totalPlatforms = tree.length;
    let totalSubApps = 0;
    let totalServices = 0;
    let activeApps = 0;

    allApps.forEach((app) => {
      const status = getApplicationUnifiedStatus(app);
      if (status === "active" || status === "healthy" || status === "running") {
        activeApps += 1;
      }
    });

    tree.forEach((parent) => {
      const sub = parent.sub_applications || [];
      totalSubApps += sub.length;
      totalServices += (parent.services || []).length;
      sub.forEach((s) => {
        totalServices += (s.services || []).length;
      });
    });

    return {
      totalPlatforms,
      totalSubApps,
      totalServices,
      totalApps: allApps.length,
      activeApps,
    };
  }, [tree, allApps]);

  const filteredTree = useMemo(() => {
    return tree.filter((parent) => {
      const term = (search || "").toLowerCase();
      if (!term) return true;
      const parentMatches =
        (parent.name && parent.name.toLowerCase().includes(term)) ||
        (parent.code && parent.code.toLowerCase().includes(term)) ||
        (parent.owner_team && parent.owner_team.toLowerCase().includes(term)) ||
        (parent.services || []).some(
          (s) =>
            (s.service_name && s.service_name.toLowerCase().includes(term)) ||
            (s.hostname && s.hostname.toLowerCase().includes(term)),
        );

      const subMatches = (parent.sub_applications || []).some(
        (sub) =>
          (sub.name && sub.name.toLowerCase().includes(term)) ||
          (sub.code && sub.code.toLowerCase().includes(term)) ||
          (sub.services || []).some(
            (s) =>
              (s.service_name && s.service_name.toLowerCase().includes(term)) ||
              (s.hostname && s.hostname.toLowerCase().includes(term)),
          ),
      );

      return parentMatches || subMatches;
    });
  }, [tree, search]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const paginatedTree = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredTree.slice(start, start + PAGE_SIZE);
  }, [filteredTree, page, PAGE_SIZE]);

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#F8FAFC",
        height: "100vh",
      }}
    >
      {/* HEADER PANEL */}
      <div
        style={{
          padding: "20px 24px 16px",
          borderBottom: "1px solid #E2E8F0",
          background: "#FFFFFF",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: "#111827",
            }}
          >
            Applications Management
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6B7280" }}>
            Define platform hierarchies, manage business microservices, and link
            infrastructure ports.
          </p>
        </div>

        {!loading && tree.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              onClick={() => handleOpenCreate("")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 6,
                background: "#1D4ED8",
                color: "#FFFFFF",
                border: "none",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                boxShadow: "0 1px 2px rgba(29, 78, 216, 0.15)",
                transition: "background 0.15s ease",
              }}
            >
              <Icon name="plus" size={14} color="#fff" />
              New Application
            </button>
          </div>
        )}
      </div>

      {/* KPI METRICS STRIP */}
      {!loading && tree.length > 0 && (
        <div
          style={{
            padding: "14px 24px",
            background: "#FFFFFF",
            borderBottom: "1px solid #E2E8F0",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              background: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: 8,
              padding: "10px 14px",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "#DBEAFE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#1D4ED8",
                flexShrink: 0,
              }}
            >
              <Icon name="layers" size={18} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#64748B",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Root Platforms
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#0F172A",
                  marginTop: 2,
                }}
              >
                {stats.totalPlatforms}
              </div>
            </div>
          </div>

          <div
            style={{
              background: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: 8,
              padding: "10px 14px",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "#F5F3FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#7C3AED",
                flexShrink: 0,
              }}
            >
              <Icon name="server" size={18} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#64748B",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Microservices / Sub-Apps
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#0F172A",
                  marginTop: 2,
                }}
              >
                {stats.totalSubApps}
              </div>
            </div>
          </div>

          <div
            style={{
              background: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: 8,
              padding: "10px 14px",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "#ECFEFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#0891B2",
                flexShrink: 0,
              }}
            >
              <Icon name="link" size={18} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#64748B",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Bound Port Services
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#0F172A",
                  marginTop: 2,
                }}
              >
                {stats.totalServices}
              </div>
            </div>
          </div>

          <div
            style={{
              background: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: 8,
              padding: "10px 14px",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "#DCFCE7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#16A34A",
                flexShrink: 0,
              }}
            >
              <Icon name="activity" size={18} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#64748B",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Active Applications
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#16A34A",
                  marginTop: 2,
                }}
              >
                {stats.activeApps}{" "}
                <span
                  style={{ fontSize: 12, fontWeight: 500, color: "#64748B" }}
                >
                  / {stats.totalApps}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SEARCH & CONTROLS TOOLBAR */}
      {!loading && tree.length > 0 && (
        <div
          style={{
            padding: "12px 24px",
            borderBottom: "1px solid #E2E8F0",
            background: "#FFFFFF",
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 260,
              maxWidth: 460,
            }}
          >
            <SearchInput
              placeholder="Search applications, code, owner team, port services..."
              value={search}
              onChange={setSearch}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={expandAll}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                background: "#FFFFFF",
                border: "1px solid #CBD5E1",
                fontSize: 12,
                fontWeight: 500,
                color: "#475569",
                cursor: "pointer",
              }}
            >
              Expand All
            </button>
            <button
              type="button"
              onClick={collapseAll}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                background: "#FFFFFF",
                border: "1px solid #CBD5E1",
                fontSize: 12,
                fontWeight: 500,
                color: "#475569",
                cursor: "pointer",
              }}
            >
              Collapse All
            </button>
          </div>
        </div>
      )}

      {/* SCROLLABLE MAIN LIST CONTENT */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px 24px",
        }}
      >
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} height={130} style={{ borderRadius: 8 }} />
            ))}
          </div>
        ) : filteredTree.length === 0 ? (
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              border: "1px solid #E5E7EB",
              padding: "24px 20px",
            }}
          >
            {tree.length > 0 ? (
              <>
                <EmptyState
                  icon="search"
                  title="No results found"
                  subtitle="No matching applications found for your search query. Try clearing the search or adjusting filters."
                />
                <div
                  style={{
                    textAlign: "center",
                    marginTop: -20,
                    marginBottom: 12,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    style={{
                      padding: "7px 16px",
                      background: "#fff",
                      color: "#374151",
                      border: "1px solid #CBD5E1",
                      borderRadius: 7,
                      cursor: "pointer",
                      fontSize: 12.5,
                      fontWeight: 600,
                    }}
                  >
                    Reset Search
                  </button>
                </div>
              </>
            ) : (
              <>
                <EmptyState
                  icon="layers"
                  title="No applications configured"
                  subtitle="Define platform hierarchies, manage business microservices, and link infrastructure ports."
                />
                <div
                  style={{
                    textAlign: "center",
                    marginTop: -20,
                    marginBottom: 12,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleOpenCreate("")}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "7px 16px",
                      borderRadius: 7,
                      background: "#1D4ED8",
                      color: "#FFFFFF",
                      border: "none",
                      fontWeight: 600,
                      fontSize: 12.5,
                      cursor: "pointer",
                      boxShadow: "0 1px 2px rgba(29, 78, 216, 0.15)",
                    }}
                  >
                    <Icon name="plus" size={13} color="#FFFFFF" />
                    New Application
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {paginatedTree.map((parent) => {
              const isExpanded = search ? true : expandedParents.has(parent.id);
              const subApps = parent.sub_applications || [];
              const directServices = parent.services || [];
              const totalServicesCount =
                directServices.length +
                subApps.reduce(
                  (acc, sub) => acc + (sub.services || []).length,
                  0,
                );

              return (
                <div
                  key={parent.id}
                  style={{
                    background: "#FFFFFF",
                    borderRadius: 10,
                    border: "1px solid #E2E8F0",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                    transition: "box-shadow 0.15s ease",
                  }}
                >
                  {/* Platform Root Header Card */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "16px 20px",
                      background: "#F8FAFC",
                      borderBottom:
                        isExpanded &&
                        (subApps.length > 0 || directServices.length > 0)
                          ? "1px solid #E2E8F0"
                          : "none",
                      gap: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        cursor: "pointer",
                        flex: 1,
                        minWidth: 280,
                      }}
                      onClick={() => toggleParentExpand(parent.id)}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleParentExpand(parent.id);
                        }}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          padding: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#64748B",
                        }}
                      >
                        <span
                          style={{
                            transform: isExpanded ? "rotate(90deg)" : "none",
                            transition: "transform 0.15s ease",
                            display: "flex",
                          }}
                        >
                          <Icon name="chevronRight" size={16} />
                        </span>
                      </button>

                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 8,
                          background: "#DBEAFE",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#1D4ED8",
                          flexShrink: 0,
                        }}
                      >
                        <Icon name="layers" size={20} />
                      </div>

                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: 15,
                              color: "#0F172A",
                            }}
                          >
                            {parent.name}
                          </span>
                          {parent.code && (
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                background: "#E2E8F0",
                                color: "#334155",
                                padding: "2px 6px",
                                borderRadius: 4,
                              }}
                            >
                              {parent.code}
                            </span>
                          )}
                          <StatusBadge entity={parent} type="application" />
                          {parent.owner_team && (
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 500,
                                color: "#475569",
                                background: "#F1F5F9",
                                padding: "2px 8px",
                                borderRadius: 12,
                                border: "1px solid #E2E8F0",
                              }}
                            >
                              Team: {parent.owner_team}
                            </span>
                          )}
                        </div>
                        {parent.description && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#64748B",
                              marginTop: 3,
                            }}
                          >
                            {parent.description}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Info & Actions */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          fontSize: 12,
                          color: "#64748B",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <Icon name="layers" size={13} color="#64748B" />
                          <strong style={{ color: "#0F172A" }}>
                            {subApps.length}
                          </strong>{" "}
                          Sub-Apps
                        </span>
                        <span>•</span>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <Icon name="link" size={13} color="#64748B" />
                          <strong style={{ color: "#0F172A" }}>
                            {totalServicesCount}
                          </strong>{" "}
                          Services
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleOpenCreate(parent.id)}
                          style={{
                            padding: "6px 12px",
                            borderRadius: 6,
                            background: "#EFF6FF",
                            color: "#1D4ED8",
                            border: "1px solid #BFDBFE",
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            transition: "all 0.15s ease",
                          }}
                          title="Add nested microservice"
                        >
                          <Icon name="plus" size={13} />
                          Add Sub-App
                        </button>

                        <button
                          type="button"
                          onClick={() => handleOpenEdit(parent)}
                          style={{
                            padding: "6px 10px",
                            borderRadius: 6,
                            background: "#FFFFFF",
                            color: "#475569",
                            border: "1px solid #CBD5E1",
                            fontSize: 12,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                          title="Edit Platform"
                        >
                          <Icon name="edit" size={13} />
                        </button>

                        <button
                          type="button"
                          onClick={() => setDeletingApp(parent)}
                          style={{
                            padding: "6px 10px",
                            borderRadius: 6,
                            background: "#FFFFFF",
                            color: "#DC2626",
                            border: "1px solid #FECACA",
                            fontSize: 12,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                          title="Decommission Platform"
                        >
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Body: Direct Services + Sub-Applications */}
                  {isExpanded && (
                    <div
                      style={{
                        padding: "16px 20px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                      }}
                    >
                      {/* Direct Platform Services */}
                      {directServices.length > 0 && (
                        <div
                          style={{
                            background: "#F8FAFC",
                            borderRadius: 8,
                            border: "1px solid #E2E8F0",
                            padding: "12px 16px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 12.5,
                                fontWeight: 600,
                                color: "#1E293B",
                              }}
                            >
                              <Icon name="link" size={13} color="#2563EB" />
                              <span>
                                Direct Platform Services (
                                {directServices.length})
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPickerTargetApp(parent)}
                              style={{
                                padding: "4px 8px",
                                borderRadius: 5,
                                background: "#EFF6FF",
                                color: "#1D4ED8",
                                border: "1px solid #BFDBFE",
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <Icon name="plus" size={11} />
                              Link Service
                            </button>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 8,
                              alignItems: "center",
                            }}
                          >
                            {directServices.map((svc) => {
                              const port =
                                svc.metadata?.port ||
                                (svc.service_name.includes(":")
                                  ? svc.service_name.split(":")[1]
                                  : null);

                              return (
                                <div
                                  key={svc.service_id}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "4px 10px",
                                    borderRadius: 6,
                                    background: "#FFFFFF",
                                    border: "1px solid #CBD5E1",
                                    fontSize: 11.5,
                                    color: "#1E293B",
                                  }}
                                >
                                  <Icon
                                    name="server"
                                    size={12}
                                    color="#2563EB"
                                  />
                                  <strong style={{ color: "#334155" }}>
                                    {svc.hostname}
                                  </strong>
                                  {port && (
                                    <span
                                      style={{
                                        color: "#0284C7",
                                        fontWeight: 600,
                                      }}
                                    >
                                      :{port}
                                    </span>
                                  )}
                                  <span style={{ color: "#64748B" }}>
                                    ({svc.service_name})
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleUnlinkService(
                                        parent.id,
                                        svc.service_id,
                                        svc.service_name,
                                      )
                                    }
                                    style={{
                                      border: "none",
                                      background: "transparent",
                                      padding: 0,
                                      marginLeft: 4,
                                      color: "#94A3B8",
                                      cursor: "pointer",
                                      display: "flex",
                                    }}
                                    title="Unlink Service"
                                  >
                                    <Icon name="x" size={12} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Sub-Applications List */}
                      {subApps.length === 0 && directServices.length === 0 ? (
                        <div
                          style={{
                            padding: "24px",
                            textAlign: "center",
                            background: "#F8FAFC",
                            borderRadius: 8,
                            border: "1px dashed #CBD5E1",
                            color: "#64748B",
                            fontSize: 13,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <div>
                            No microservices or port services linked under{" "}
                            <strong style={{ color: "#0F172A" }}>
                              {parent.name}
                            </strong>{" "}
                            yet.
                          </div>
                          <div style={{ display: "flex", gap: 10 }}>
                            <button
                              type="button"
                              onClick={() => handleOpenCreate(parent.id)}
                              style={{
                                padding: "6px 14px",
                                borderRadius: 6,
                                background: "#EFF6FF",
                                color: "#1D4ED8",
                                border: "1px solid #BFDBFE",
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                            >
                              <Icon name="plus" size={13} />
                              Add Microservice
                            </button>
                            <button
                              type="button"
                              onClick={() => setPickerTargetApp(parent)}
                              style={{
                                padding: "6px 14px",
                                borderRadius: 6,
                                background: "#F0FDF4",
                                color: "#16A34A",
                                border: "1px solid #BBF7D0",
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                            >
                              <Icon name="link" size={13} />
                              Link Port Services
                            </button>
                          </div>
                        </div>
                      ) : (
                        subApps.length > 0 && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#475569",
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <Icon name="server" size={13} color="#7C3AED" />
                              <span>
                                Microservices & Components ({subApps.length})
                              </span>
                            </div>

                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr",
                                gap: 10,
                              }}
                            >
                              {subApps.map((sub) => {
                                const services = sub.services || [];
                                return (
                                  <div
                                    key={sub.id}
                                    style={{
                                      border: "1px solid #E2E8F0",
                                      borderRadius: 8,
                                      padding: "14px 16px",
                                      background: "#FFFFFF",
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: 14,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <div>
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: 24,
                                              height: 24,
                                              borderRadius: 5,
                                              background: "#F5F3FF",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              color: "#7C3AED",
                                              flexShrink: 0,
                                            }}
                                          >
                                            <Icon name="server" size={13} />
                                          </div>
                                          <span
                                            style={{
                                              fontWeight: 600,
                                              fontSize: 14,
                                              color: "#0F172A",
                                            }}
                                          >
                                            {sub.name}
                                          </span>
                                          {sub.code && (
                                            <span
                                              style={{
                                                fontSize: 10.5,
                                                fontWeight: 600,
                                                background: "#F1F5F9",
                                                color: "#475569",
                                                padding: "1px 6px",
                                                borderRadius: 4,
                                              }}
                                            >
                                              {sub.code}
                                            </span>
                                          )}
                                          <StatusBadge
                                            entity={sub}
                                            type="application"
                                          />
                                          {sub.owner_team && (
                                            <span
                                              style={{
                                                fontSize: 11,
                                                color: "#64748B",
                                              }}
                                            >
                                              • Team: {sub.owner_team}
                                            </span>
                                          )}
                                        </div>
                                        {sub.description && (
                                          <div
                                            style={{
                                              fontSize: 12,
                                              color: "#64748B",
                                              marginTop: 4,
                                            }}
                                          >
                                            {sub.description}
                                          </div>
                                        )}
                                      </div>

                                      <div
                                        style={{
                                          display: "flex",
                                          gap: 6,
                                          alignItems: "center",
                                        }}
                                      >
                                        <button
                                          type="button"
                                          onClick={() => handleOpenEdit(sub)}
                                          style={{
                                            padding: "5px 8px",
                                            borderRadius: 5,
                                            background: "#FFFFFF",
                                            color: "#64748B",
                                            border: "1px solid #CBD5E1",
                                            fontSize: 11.5,
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                          title="Edit Microservice"
                                        >
                                          <Icon name="edit" size={12} />
                                        </button>

                                        <button
                                          type="button"
                                          onClick={() => setDeletingApp(sub)}
                                          style={{
                                            padding: "5px 8px",
                                            borderRadius: 5,
                                            background: "#FFFFFF",
                                            color: "#DC2626",
                                            border: "1px solid #FECACA",
                                            fontSize: 11.5,
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                          title="Decommission Microservice"
                                        >
                                          <Icon name="trash" size={12} />
                                        </button>
                                      </div>
                                    </div>

                                    {/* Attached Port Services Chips */}
                                    <div
                                      style={{
                                        marginTop: 12,
                                        paddingTop: 10,
                                        borderTop: "1px solid #F1F5F9",
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 8,
                                        alignItems: "center",
                                      }}
                                    >
                                      {services.length === 0 ? (
                                        <div
                                          style={{
                                            fontSize: 11.5,
                                            color: "#94A3B8",
                                            fontStyle: "italic",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                          }}
                                        >
                                          <span>No services linked yet.</span>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setPickerTargetApp(sub)
                                            }
                                            style={{
                                              padding: "2px 8px",
                                              borderRadius: 4,
                                              background: "#EFF6FF",
                                              color: "#1D4ED8",
                                              border: "1px solid #BFDBFE",
                                              fontSize: 11,
                                              fontWeight: 600,
                                              cursor: "pointer",
                                            }}
                                          >
                                            + Link Port
                                          </button>
                                        </div>
                                      ) : (
                                        <>
                                          {services.map((svc) => {
                                            const port =
                                              svc.metadata?.port ||
                                              (svc.service_name.includes(":")
                                                ? svc.service_name.split(":")[1]
                                                : null);

                                            return (
                                              <div
                                                key={svc.service_id}
                                                style={{
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  gap: 6,
                                                  padding: "3px 8px",
                                                  borderRadius: 5,
                                                  background: "#F8FAFC",
                                                  border: "1px solid #CBD5E1",
                                                  fontSize: 11,
                                                  color: "#1E293B",
                                                }}
                                              >
                                                <Icon
                                                  name="server"
                                                  size={12}
                                                  color="#2563EB"
                                                />
                                                <strong
                                                  style={{ color: "#334155" }}
                                                >
                                                  {svc.hostname}
                                                </strong>
                                                {port && (
                                                  <span
                                                    style={{
                                                      color: "#0284C7",
                                                      fontWeight: 600,
                                                    }}
                                                  >
                                                    :{port}
                                                  </span>
                                                )}
                                                <span
                                                  style={{ color: "#64748B" }}
                                                >
                                                  ({svc.service_name})
                                                </span>
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleUnlinkService(
                                                      sub.id,
                                                      svc.service_id,
                                                      svc.service_name,
                                                    )
                                                  }
                                                  style={{
                                                    border: "none",
                                                    background: "transparent",
                                                    padding: 0,
                                                    marginLeft: 4,
                                                    color: "#94A3B8",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                  }}
                                                  title="Unlink Service"
                                                >
                                                  <Icon name="x" size={12} />
                                                </button>
                                              </div>
                                            );
                                          })}
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setPickerTargetApp(sub)
                                            }
                                            style={{
                                              padding: "3px 8px",
                                              borderRadius: 5,
                                              background: "#F8FAFC",
                                              color: "#2563EB",
                                              border: "1px dashed #93C5FD",
                                              fontSize: 11,
                                              fontWeight: 600,
                                              cursor: "pointer",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: 3,
                                            }}
                                          >
                                            <Icon name="plus" size={10} />
                                            Link
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Pagination Controls */}
            {filteredTree.length > PAGE_SIZE && (
              <div
                style={{
                  marginTop: 8,
                  borderRadius: 8,
                  overflow: "hidden",
                  border: "1px solid #E5E7EB",
                }}
              >
                <Pagination
                  page={page}
                  total={filteredTree.length}
                  pageSize={PAGE_SIZE}
                  onChange={setPage}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {isEditModalOpen && (
        <Modal
          title={editingApp ? "Edit Application" : "Create New Application"}
          onClose={() => setIsEditModalOpen(false)}
          width={520}
        >
          <form
            onSubmit={handleSaveApplication}
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            <div>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#334155",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Application Name *
              </label>
              <input
                type="text"
                required
                placeholder="e.g. CXSphere Platform or Checkout Service"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: 6,
                  border: "1px solid #CBD5E1",
                  fontSize: 13,
                }}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#334155",
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Application Code
                </label>
                <input
                  type="text"
                  placeholder="e.g. CXS-PLATFORM"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 6,
                    border: "1px solid #CBD5E1",
                    fontSize: 13,
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#334155",
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Owner Team
                </label>
                <input
                  type="text"
                  placeholder="e.g. Platform SRE"
                  value={formData.owner_team}
                  onChange={(e) =>
                    setFormData({ ...formData, owner_team: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 6,
                    border: "1px solid #CBD5E1",
                    fontSize: 13,
                  }}
                />
              </div>
            </div>

            <div>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#334155",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Parent Platform (Optional for Root Applications)
              </label>
              <select
                value={formData.parent_id}
                onChange={(e) =>
                  setFormData({ ...formData, parent_id: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: 6,
                  border: "1px solid #CBD5E1",
                  fontSize: 13,
                  background: "#FFFFFF",
                }}
              >
                <option value="">— None (Top-Level Platform) —</option>
                {allApps
                  .filter((a) => !forbiddenParentIds.has(a.id))
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} {a.code ? `(${a.code})` : ""}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#334155",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Description
              </label>
              <textarea
                rows={3}
                placeholder="Operational purpose, SLAs, or business critical functions..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: 6,
                  border: "1px solid #CBD5E1",
                  fontSize: 13,
                  resize: "vertical",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                style={{
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "1px solid #CBD5E1",
                  background: "#FFFFFF",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: "8px 18px",
                  borderRadius: 6,
                  border: "none",
                  background: "#1D4ED8",
                  color: "#FFFFFF",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                {saving
                  ? "Saving..."
                  : editingApp
                    ? "Save Changes"
                    : "Create Application"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Decommission Confirmation Modal */}
      <ConfirmModal
        isOpen={Boolean(deletingApp)}
        title="Decommission Application"
        onClose={() => setDeletingApp(null)}
        onConfirm={handleDeleteApplication}
        loading={saving}
        confirmText="Confirm Decommission"
        variant="danger"
      >
        {deletingApp && (
          <p style={{ margin: 0, fontSize: 13, color: "#475569" }}>
            Are you sure you want to decommission{" "}
            <strong style={{ color: "#0F172A" }}>{deletingApp.name}</strong>?
            This will remove active service mappings while preserving historical
            audit data.
          </p>
        )}
      </ConfirmModal>

      {/* Service Picker Modal */}
      {pickerTargetApp && (
        <ServerServicePickerModal
          targetApp={pickerTargetApp}
          onClose={() => setPickerTargetApp(null)}
          onAttached={loadData}
        />
      )}
    </div>
  );
}
